import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify';
import { getTotalWorkHoursReport, exportTotalWorkHoursReportCSV } from '../../../axios/Report/ReportUrlApi';
import dayjs from 'dayjs';
import { useAuth } from '../../../context/AuthContext'
import {
  ReportPageHeader,
  ReportDateFilterRow,
} from './ReportSharedComponents';

const TIME_FIELDS = [
  'productiveWorkHours',
  'totalWorkHours',
  'totalShiftWorkHours',
  'productiveHours',
  'totalLeave',
  'totalLeaveUnExcused',
  'totalHoliday',
  'totalMiddleBreakTime',
  'workTime',
];

const parseTimeToSeconds = (value) => {
  if (value == null || value === '' || value === '-') return 0;
  const parts = String(value).trim().split(':').map(Number);
  if (!parts.length || parts.some((n) => !Number.isFinite(n))) return 0;
  const [h = 0, m = 0, s = 0] = parts;
  return h * 3600 + m * 60 + s;
};

const secondsToHhMmSs = (totalSeconds) => {
  const secs = Math.max(0, Math.round(Number(totalSeconds) || 0));
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const isLeaveUnExcusedViolation = (totalLeaveUnExcused, maximumLeaveDuration) => {
  const maxSeconds = parseTimeToSeconds(maximumLeaveDuration);
  if (maxSeconds <= 0) return false;
  return parseTimeToSeconds(totalLeaveUnExcused) > maxSeconds;
};

/**
 * Daily: giữ nhiều dòng.
 * Weekly / Monthly / Custom: gom theo nhân viên thành 1 dòng,
 * cộng thời gian các cột, from/to theo khoảng chọn, violationCount = số ngày vi phạm.
 */
export const processTotalWorkHoursRows = (
  rows = [],
  { sumBy = '1', startDate, endDate, maximumLeaveDuration } = {}
) => {
  const withViolation = (rows || []).map((row) => ({
    ...row,
    violationCount: isLeaveUnExcusedViolation(
      row.totalLeaveUnExcused,
      maximumLeaveDuration
    )
      ? 1
      : 0,
  }));

  if (!sumBy || sumBy === '1') {
    return withViolation;
  }

  const rangeFrom = dayjs(startDate).isValid()
    ? dayjs(startDate).format('DD/MM/YYYY')
    : withViolation[0]?.from || '';
  const rangeTo = dayjs(endDate).isValid()
    ? dayjs(endDate).format('DD/MM/YYYY')
    : withViolation[0]?.to || '';

  const grouped = new Map();

  withViolation.forEach((row) => {
    const key =
      row.employeeCode ||
      row.userId ||
      row.email ||
      row.fullName ||
      JSON.stringify([row.fullName, row.email]);

    if (!grouped.has(key)) {
      grouped.set(key, {
        ...row,
        from: rangeFrom,
        to: rangeTo,
        workDate: null,
        workTime: null,
        violationCount: 0,
        _timeSums: Object.fromEntries(TIME_FIELDS.map((f) => [f, 0])),
      });
    }

    const acc = grouped.get(key);
    acc.violationCount += Number(row.violationCount) || 0;
    TIME_FIELDS.forEach((field) => {
      acc._timeSums[field] += parseTimeToSeconds(row[field]);
    });
  });

  return Array.from(grouped.values()).map(({ _timeSums, ...rest }) => {
    const summed = { ...rest };
    TIME_FIELDS.forEach((field) => {
      summed[field] = secondsToHhMmSs(_timeSums[field] || 0);
    });
    return summed;
  });
};

const TotalWorkHoursReport = (props) => {
    const { onBack, setLoading, handleFilter, dataFilter, page, pageSize, getTabName, profile, isAdmin, totalPages } = props;
    const { dateTimeFormat, maximumLeaveDuration } = useAuth();
    const { t, i18n } = useTranslation()
    const currentLang = i18n.language;

    function formatDateTime(date) {
        if (!date) return null;
        const d = dayjs(date);
        if (!d.isValid()) return null;
        return d.format('YYYY-MM-DD HH:mm:ss');
    }

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const initialFormData = {
        sumBy: "1",
        startDate: new Date(yesterday),
        endDate: new Date(yesterday),
        showTime: "h-m-s",
    }

    const [formData, setFormData] = useState(initialFormData)
    const lastTablePayloadRef = useRef(null);

    // Luôn lấy daily + HH:mm:ss từ API để check/gom chính xác; format giờ & gom chu kỳ làm trên FE
    const isDaily = formData.sumBy === "1";
    const bodyRequest = {
        "reportCycleTypeId": 1,
        "fromDate": formData.startDate?.target?.value
            ? formatDateTime(formData.startDate.target.value)
            : formatDateTime(formData.startDate),
        "toDate": formData.endDate?.target?.value
            ? formatDateTime(formData.endDate.target.value)
            : formatDateTime(formData.endDate),
        "userId": isAdmin ? (dataFilter?.userName ? dataFilter.userName : null) : profile.userId,
        "userTypeId": null,
        "shiftId": dataFilter?.shift ? dataFilter.shift : '',
        "groupId": dataFilter?.userGroup ? dataFilter.userGroup : null,
        // Weekly/Monthly/Custom: lấy đủ ngày trong khoảng để gom đúng số lần vi phạm
        "pageNo": isDaily ? page + 1 : 1,
        "recordPerPage": isDaily ? pageSize : 5000,
        "showTime": "h-m-s",
        "lang": currentLang
    }

    const fetchData = async () => {
        let convertShiftId = bodyRequest?.shiftId;
        if (typeof convertShiftId === 'object' && Array.isArray(convertShiftId)) {
            convertShiftId = convertShiftId.join(',');
        }
        convertShiftId = String(convertShiftId ?? '').replace(/^(0,)+/, '');

        const payload = {
            ...bodyRequest,
            shiftId: convertShiftId,
        };
        try {
            setLoading(true);
            const response = await getTotalWorkHoursReport(payload);
            if (response.data.message === 'Successful' || response.data.message === 'Thành công') {
                const processedData = processTotalWorkHoursRows(response.data.data || [], {
                    sumBy: formData.sumBy,
                    startDate: formData.startDate,
                    endDate: formData.endDate,
                    maximumLeaveDuration,
                });

                const apiPagination = response.data.pagination || {};
                // Giữ pageSize UI (10/20/50/100) — không ghi đè bằng số dòng đã gom
                const safePageSize = pageSize || apiPagination.pageSize || 50;
                const nextTableData = {
                    ...response.data,
                    data: processedData,
                    showTime: formData.showTime,
                    sumBy: formData.sumBy,
                    pagination: isDaily
                        ? {
                            ...apiPagination,
                            currentPage: (apiPagination.currentPage || 1) - 1,
                            pageSize: safePageSize,
                          }
                        : {
                            ...apiPagination,
                            currentPage: 0,
                            totalElements: processedData.length,
                            totalPages: Math.max(
                              1,
                              Math.ceil(processedData.length / safePageSize)
                            ),
                            pageSize: safePageSize,
                          },
                };
                lastTablePayloadRef.current = nextTableData;
                handleFilter(nextTableData);
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            if (error.response) {
                if (error.response.status !== 401) {
                    toast.error(t(error?.response.data));
                }
            } else {
                toast.error(t('common.network-timeout'));
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if ((dataFilter?.userName || page || pageSize)) {
            fetchData();
        }
        // Không phụ thuộc formData.showTime — format giờ làm trên FE
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        dataFilter.userName,
        formData.sumBy,
        formData.startDate,
        formData.endDate,
        page,
        pageSize,
        bodyRequest.shiftId,
        maximumLeaveDuration,
    ]);

    // Đổi định dạng giờ: chỉ cập nhật metadata bảng, không refetch
    useEffect(() => {
        if (!lastTablePayloadRef.current) return;
        const nextTableData = {
            ...lastTablePayloadRef.current,
            showTime: formData.showTime,
        };
        lastTablePayloadRef.current = nextTableData;
        handleFilter(nextTableData);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.showTime]);

    useEffect(() => {
        getTabName("TotalWorkHoursReport")
    }, []);

    function getCurrentTimestamp() {
        const now = new Date();
        const yyyy = now.getFullYear();
        const MM = String(now.getMonth() + 1).padStart(2, '0');
        const dd = String(now.getDate()).padStart(2, '0');
        const HH = String(now.getHours()).padStart(2, '0');
        const mm = String(now.getMinutes()).padStart(2, '0');
        const ss = String(now.getSeconds()).padStart(2, '0');
        return `${yyyy}${MM}${dd}${HH}${mm}${ss}`;
    }

    const exportCSV = async () => {
        let convertShiftId = bodyRequest?.shiftId;
        if (typeof convertShiftId === 'object' && Array.isArray(convertShiftId)) {
            convertShiftId = convertShiftId.join(',');
        }

        const payload = {
            ...bodyRequest,
            shiftId: convertShiftId,
            formatDate: dateTimeFormat,
            pageNo: totalPages,
            // API vẫn lấy daily HH:mm:ss; BE gom theo sumBy và format theo showTime
            reportCycleTypeId: 1,
            showTime: "h-m-s",
            sumBy: formData.sumBy,
            uiShowTime: formData.showTime,
            maximumLeaveDuration: maximumLeaveDuration || "00:00:00",
        };

        try {
            setLoading(true);
            const response = await exportTotalWorkHoursReportCSV(payload);
            if (response) {
                const blob = new Blob([response.data], { type: 'application/octet-stream' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${t("export.TotalWorkHoursReport")}_${getCurrentTimestamp()}.xlsx`;
                document.body.appendChild(a);
                a.click();
                a.remove();
            }
        } catch (error) {
            if (error.response?.status !== 401) {
                toast.error(t(error?.response?.data));
            } else if (!error.response) {
                toast.error(t('common.network-timeout'));
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className='total-work-hours-report'>
            <ReportPageHeader
                onBack={onBack}
                titleKey="common.total-work-hours-report"
                onExport={exportCSV}
                startDate={formData.startDate}
                endDate={formData.endDate}
                onUpdateSuccess={fetchData}
            />
            <div data-tour="reports-filter-input">
                <ReportDateFilterRow
                    formData={formData}
                    setFormData={setFormData}
                />
            </div>
        </div>
    )
}

export default TotalWorkHoursReport
